
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BookingItemsStore } from '@fry/booking/lib/booking-items';
import { BookingsStore } from '@fry/booking/lib/bookings';
import { FryCommonModule } from '@fry/components/common';
import { InteractiveTableModule } from '@fry/components/common/list/interactive-table';
import { InteractiveTableOptions } from '@fry/components/common/list/interactive-table/interactive-table.component';
import { MaterialModule } from '@fry/components/material';
import { OidcService } from '@fry-it/risr-core';
import { ListDefaults, TableService } from '@fry/lib/list';
import { UnboxedModule } from '@fry/ui';
import { LoadingComponent } from '@fry/ui-material/loading';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { EligibilityViewComponent } from '../eligibility-view/eligibility-view.compoent';
import { AlertComponent } from '@fry/ui-material/alert';
import { UIBadgeModule } from '@fry/ui/badge';
import { SecurityService } from '@fry/lib/security';

interface UserInfo {
    user: string;
    currentUser: string;
    isAnonymous: boolean;
    isRemoteUser: boolean;
}


@Component({
    selector: 'eas-booking-apply',
    templateUrl: './booking-apply.component.html',
    styles: [`
        .mdc-card {
            margin-bottom: 16px;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        TableService
    ],
    imports: [
    RouterModule,
    FryCommonModule,
    UnboxedModule,
    InteractiveTableModule,
    MaterialModule,
    MatDialogModule,
    LoadingComponent,
    AlertComponent,
    EligibilityViewComponent,
    UIBadgeModule
],
    standalone: true
})
export class BookingApplyComponent {

    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private bookingItems = inject(BookingItemsStore);
    private bookingStore = inject(BookingsStore);
    private tableService = inject(TableService);
    private oidc = inject(OidcService);
    private security = inject(SecurityService);

    $loaded = signal<boolean>(false);
    $error = signal<string | null>(null);
    $operationInProgress = signal<boolean>(false);
    $tableLoaded = signal<boolean>(false);

    $viewEligibility = signal<boolean>(false);

    hasManagePermission = toSignal(
        this.route.params.pipe(
            switchMap(params => {
                if (params['user']) {
                    return this.security.hasPermissionFor('bookings.manage', params['user'], false, params['bid']);
                } else {
                    return this.security.hasPermission('bookings.manage');
                }
            })
        )
    );

    $params = toSignal(this.route.params.pipe(map(params => {
        return {
            bid: params['bid'],
            boid: params['boid'],
            user: params['user']
        }
    })), { initialValue: { bid: undefined, boid: undefined, user: undefined} });

    $userInfo = computed<UserInfo>(() => {
        const currentUser = this.oidc.currentUser().username || 'anonymous';
        const user = this.$params()?.user || currentUser;
        return {
            user: user,
            currentUser: currentUser,
            isAnonymous: currentUser === 'anonymous',
            isRemoteUser: user !== currentUser
        }
    })

    $bookingOption = toSignal(
        this.route.params.pipe(switchMap(params => {
        if (!params['bid'] || !params['boid']) {
            return of(null);
        }
        return this.bookingItems.getBookableOption(params['boid'], params['bid']);
    })));

    $applyInfo = toSignal(this.route.params.pipe(switchMap(params => {
        if (params['bid'] && params['boid']) {
            return this.loadApplyInfo(params['bid'], params['boid']);
        }
        return of(null);
    })), { initialValue: null });

    $canApply = computed(() => this.$applyInfo()?.can_apply ?? false);
    $eligibility = computed(() => this.$applyInfo()?.eligibility ?? null);
    $availableCapacity = computed(() => this.$applyInfo()?.available_capacity ?? -1);

    $bookingCount = signal<number>(0);

    $applicationState = computed(() => {
        const eligibility = this.$eligibility();
        const showApply = eligibility?.overall && eligibility?.available_capacity !== 0 && this.$canApply() && !this.$operationInProgress();
        const showForceApply = this.hasManagePermission() && eligibility?.available_capacity !== 0 && !showApply;
        const isTreeLike = eligibility?.rules?.args?.length > 0 && eligibility?.rules?.args[0].args?.length > 0;

        return {
            isEligible:  eligibility?.overall || false,
            eligibilityTree: eligibility?.rules,
            hasEligibilityTree: isTreeLike,
            eligibilityDescription: eligibility?.description || '',
            capacityAvailable: this.$availableCapacity() === -1 || this.$availableCapacity() > 0,
            showApply: showApply,
            showForceApply: showForceApply,
        }
    });


    public tableComponentOptions: InteractiveTableOptions = {
        enableColumns: false,
        enableFilters: false,
        enablePagination: true,
        enableControls: false,
        itemSingleLabel: $localize `booking`,
        itemPluralLabel: $localize `bookings`,
    };

    ngOnInit() {
        this.route.params.pipe(
            switchMap(params => {
                return this.loadBookingCount(params.bid)
            }),
            switchMap((bookingCount) => {
                this.$bookingCount.set(bookingCount);
                if (bookingCount > 0) {
                    return this.initializeTable();
                }
                return of(null);
            }),
        ).subscribe({
            next: result => {
                console.log(result);
            },
            error: error => {
                this.$error.set(error.toString());
            }
        });
    }

    loadApplyInfo(bid: string, boid: string) {
        this.$loaded.set(false);
        return this.bookingStore.applyInfo(this.$userInfo().user, bid, boid, {}).pipe(
            tap(_ => {
                this.$loaded.set(true);
            }),
            catchError(err => {
                this.$loaded.set(true);
                this.$error.set(err.toString());
                return of(null);
            })
        )
    }

    loadBookingCount(bid: string): Observable<any> {
        return this.bookingStore.search({
            filter: {
                user: this.$userInfo().user,
                bookingItem: bid
            },
            size: 0
        }).pipe(
            map(res => res.meta.total)
        )
    };

    onApply(forceApply: boolean = false) {
        this.$error.set(null);
        if (!forceApply && !this.$applicationState().showApply) {
            return;
        };
        if (forceApply && !this.$applicationState().showForceApply) {
            return;
        };
        const bookingOption = this.$bookingOption();
        const bid = bookingOption?.bookingItem._id;
        const boid = bookingOption?.id;
        if (bid && boid) {
            this.$operationInProgress.set(true);
            this.bookingStore.apply(this.$userInfo().user, bid, boid, {
                ignoreWindow: forceApply,
                ignoreEligibility: forceApply,
                skipInitialCheck: forceApply
            }).subscribe({
                next: res => {
                    this.router.navigate(['/bookings', res.result.id, 'edit']);
                    this.$operationInProgress.set(false);
                },
                error: err => {
                    console.log(err);
                    this.$error.set(err.toString());
                    this.$operationInProgress.set(false);
                }
            });
        }
    }

    toggleViewEligibility() {
        this.$viewEligibility.update(val => !val);
    }

    initializeTable() {

        const defaults: ListDefaults = {
            listType: 'table',
            engine: 'es',
            pageSize: 10,
            hideFilters: ['__all__'],
            fixedSearchModel: {
                filter: {
                    bookingItem: [this.$params().bid],
                    user: this.$userInfo().user
                },
                sort: { sort_on: 'createdDate', sort_order: 'desc' },
                select: ["bookingItem", "bookingOption", "stateName", "createdDate", "modifiedDate", "view"],
                options: {
                    includeFormFields: false
                },
                context: {
                    filter: {
                        bookingItem: [this.$params().bid],
                    },
                    user: this.$userInfo().user
                }
            },
        };

        return this.tableService.setupWith(this.bookingStore, defaults, 'general')
            .pipe(tap(() => this.$tableLoaded.set(true)));
    }
}
